import React, {ReactElement, useState} from "react";
import {IconButton, Typography} from "@mui/material";
import {ChevronRightOutlined, DeleteOutlined, ExpandMoreOutlined, LabelOutlined} from "@mui/icons-material";
import {PD_SM} from "@/shared/dimens";
import {colorHighlightAlt, mediumGray} from "@/shared/colors";
import {LabelerTask, LabelerTaskCase} from "@/fyneai-shared/types";
import {ShapeCanvasElement} from "../types";

type LabelTreeNode = {
  answerClass: string,
  labels: ShapeCanvasElement[],
};

function buildTree(case_: LabelerTaskCase, elements: ShapeCanvasElement[]): LabelTreeNode[] {
  const classMap = new Map<string, ShapeCanvasElement[]>();
  const classes = case_.getTask().answerClasses || [];
  for (const cls of classes) {
    classMap.set(cls, []);
  }
  classMap.set("", []);
  for (const el of elements) {
    const cls = el.answerClass || "";
    if (!classMap.has(cls)) {
      classMap.set(cls, []);
    }
    classMap.get(cls).push(el);
  }
  const nodes: LabelTreeNode[] = [];
  for (const cls of classes) {
    nodes.push({answerClass: cls, labels: classMap.get(cls)});
  }
  const unassigned = classMap.get("");
  if (unassigned.length > 0) {
    nodes.push({answerClass: "", labels: unassigned});
  }
  return nodes;
}

function LabelTreeNodeView({node, selectedIds, onSelect, onDelete}: {
  node: LabelTreeNode,
  selectedIds: string[],
  onSelect: (id: string) => void,
  onDelete: (id: string) => void,
}): ReactElement {
  const [expanded, setExpanded] = useState(true);
  const displayName = node.answerClass || "Unassigned";
  return <div>
    <div
      onClick={() => setExpanded(!expanded)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: `${PD_SM / 2}px ${PD_SM}px`,
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {expanded
        ? <ExpandMoreOutlined style={{fontSize: 18, color: mediumGray}}/>
        : <ChevronRightOutlined style={{fontSize: 18, color: mediumGray}}/>}
      <Typography variant="body2" style={{fontWeight: 600, flexGrow: 1}}>
        {displayName}
      </Typography>
      <Typography variant="caption" style={{color: mediumGray}}>
        {node.labels.length}
      </Typography>
    </div>
    {expanded && node.labels.map(label => {
      const isSelected = selectedIds.includes(label.id);
      return <div
        key={label.id}
        onClick={() => onSelect(label.id)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: `${PD_SM / 2}px ${PD_SM}px ${PD_SM / 2}px ${PD_SM * 3}px`,
          cursor: "pointer",
          background: isSelected ? colorHighlightAlt : undefined,
          color: isSelected ? "black" : undefined,
        }}
        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = mediumGray + "18"; }}
        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = ""; }}
      >
        <LabelOutlined style={{fontSize: 16, color: mediumGray}}/>
        <Typography variant="body2" style={{flexGrow: 1}} noWrap>
          {label.name}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onDelete(label.id); }}
          style={{padding: 2}}
        >
          <DeleteOutlined style={{fontSize: 16, color: mediumGray}}/>
        </IconButton>
      </div>;
    })}
  </div>;
}

export function LabelsSidebarView({case_, elements, selectedIds, onSelect, onDelete}: {
  case_: LabelerTaskCase,
  elements: ShapeCanvasElement[],
  selectedIds: string[],
  onSelect: (id: string) => void,
  onDelete: (id: string) => void,
}): ReactElement {
  const tree = buildTree(case_, elements);
  return <div style={{flexGrow: 1, padding: `${PD_SM}px 0`}}>
    {tree.map(node => (
      <LabelTreeNodeView
        key={node.answerClass || "__unassigned"}
        node={node}
        selectedIds={selectedIds}
        onSelect={onSelect}
        onDelete={onDelete}
      />
    ))}
    {tree.length === 0 && (
      <Typography variant="body2" style={{color: mediumGray, padding: PD_SM, textAlign: "center"}}>
        No labels yet
      </Typography>
    )}
  </div>;
}
